import _async_to_generator from "@swc/helpers/src/_async_to_generator.mjs";
import _define_property from "@swc/helpers/src/_define_property.mjs";
import _ts_generator from "@swc/helpers/src/_ts_generator.mjs";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import cx from "classnames";
import { Primary, Secondary, SvgIcon, Text, designTokens } from "@cinch-labs/design-system";
import { useViewport, getLastCheckoutStage, returnToCheckoutStage } from "@cinch-labs/shared-util";
import { checkOrderHasExpired, getBasketBannerClosed, setBasketBannerClosed } from "../utils";
import BasketBannerVehicleInformation from "../vehicle-information/vehicle-information";
import { basketBannerCloseBannerEvent, basketBannerNoLongerWantVehicleClickEvent, basketBannerReturnToCheckoutEvent } from "./banner-analytics";
import styles from "./banner.module.css";
import { useAdobeTarget } from "@cinch-labs/adobe";
export function Banner(param) {
    var activeOrder = param.activeOrder, _locale = param.locale, locale = _locale === void 0 ? {
        orderActive: {
            buttonText: "Return to checkout",
            cancelText: "I don't want this car"
        }
    } : _locale, extendOrderExpiry = param.extendOrderExpiry, refreshActiveOrder = param.refreshActiveOrder, sendDigitalDataEvent = param.sendDigitalDataEvent, reportDatadogError = param.reportDatadogError, accessToken = param.accessToken, _adminFeeFlag = param.adminFeeFlag, adminFeeFlag = _adminFeeFlag === void 0 ? false : _adminFeeFlag, adminFeeValueInPence = param.adminFeeValueInPence, cancelOrder = param.cancelOrder;
    var ref;
    var basketBannerState = getBasketBannerClosed();
    var ref1 = useState(false), returnToCheckoutButtonLoading = ref1[0], setReturnToCheckoutButtonLoading = ref1[1];
    var ref2 = useState(basketBannerState), hideBasketBanner = ref2[0], setHideBasketBanner = ref2[1];
    var deepPurple = designTokens.customProperties.ds.color.deepPurple;
    var dontWantCarExperiment = ((ref = useAdobeTarget("I_DONT_WANT_THIS_CAR")) === null || ref === void 0 ? void 0 : ref.ExperienceId) === "1";
    var vw = useViewport({
        updateOnResize: true,
        updateOnOrientationChange: true
    }).vw;
    var isMobile = vw < 768;
    useEffect(function() {
        checkOrderHasExpired(activeOrder, function() {
            setHideBasketBanner(true);
            setBasketBannerClosed(true);
        });
    }, []);
    var extendExpiry = function() {
        var _ref = _async_to_generator(function() {
            var error;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        if (activeOrder === undefined || accessToken === undefined) return [
                            2
                        ];
                        setReturnToCheckoutButtonLoading(true);
                        _state.label = 1;
                    case 1:
                        _state.trys.push([
                            1,
                            3,
                            ,
                            4
                        ]);
                        return [
                            4,
                            extendOrderExpiry(activeOrder.id, accessToken)
                        ];
                    case 2:
                        _state.sent();
                        return [
                            3,
                            4
                        ];
                    case 3:
                        error = _state.sent();
                        reportDatadogError(new Error("Extend Expiry Error"), error);
                        return [
                            3,
                            4
                        ];
                    case 4:
                        setReturnToCheckoutButtonLoading(false);
                        return [
                            2
                        ];
                }
            });
        });
        return function extendExpiry() {
            return _ref.apply(this, arguments);
        };
    }();
    var handleCloseBanner = useCallback(function() {
        setHideBasketBanner(true);
        setBasketBannerClosed(true);
        sendDigitalDataEvent(basketBannerCloseBannerEvent());
    }, []);
    var handleCarNoLongerWanted = function() {
        if (activeOrder === undefined || accessToken === undefined) return;
        setHideBasketBanner(true);
        setBasketBannerClosed(true);
        sendDigitalDataEvent(basketBannerNoLongerWantVehicleClickEvent());
        var reason = "customer no longer wants car";
        cancelOrder && cancelOrder(activeOrder.id, reason, accessToken);
    };
    var handleReturnToCheckout = useCallback(/*#__PURE__*/ _async_to_generator(function() {
        var lastVisitedStage;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    if (activeOrder === undefined) return [
                        2
                    ];
                    return [
                        4,
                        extendExpiry()
                    ];
                case 1:
                    _state.sent();
                    lastVisitedStage = getLastCheckoutStage(activeOrder);
                    return [
                        4,
                        refreshActiveOrder()
                    ];
                case 2:
                    _state.sent();
                    if (lastVisitedStage) {
                        returnToCheckoutStage(lastVisitedStage.stage, activeOrder);
                        sendDigitalDataEvent(basketBannerReturnToCheckoutEvent());
                    }
                    return [
                        2
                    ];
            }
        });
    }), [
        activeOrder
    ]);
    return activeOrder ? /*#__PURE__*/ _jsx("div", {
        className: cx(styles["banner"], _define_property({}, styles["hideBasketBanner"], hideBasketBanner)),
        children: /*#__PURE__*/ _jsx("section", {
            role: "dialog",
            "aria-labelledby": "active-order-banner",
            "aria-describedby": "description-of-active-order-vehicle",
            "aria-hidden": hideBasketBanner,
            className: styles["bannerSection"],
            children: isMobile ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Text, {
                        "data-testid": "mobileYourOrder",
                        className: styles["bannerHeading"],
                        id: "active-order-banner",
                        children: "Your order"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        "aria-label": "Close Active Order Banner",
                        "data-testid": "closeBanner",
                        className: styles["closeIconButton"],
                        onClick: function() {
                            return handleCloseBanner();
                        },
                        children: /*#__PURE__*/ _jsx(SvgIcon, {
                            name: "cross",
                            height: 12,
                            width: 12,
                            stroke: deepPurple,
                            strokeWidth: "2"
                        })
                    }),
                    /*#__PURE__*/ _jsx(BasketBannerVehicleInformation, {
                        sendDigitalDataEvent: sendDigitalDataEvent,
                        vehicle: activeOrder === null || activeOrder === void 0 ? void 0 : activeOrder.vehicle,
                        adminFeeFlag: adminFeeFlag,
                        adminFeeValueInPence: adminFeeValueInPence
                    }),
                    dontWantCarExperiment ? /*#__PURE__*/ _jsxs("div", {
                        className: styles.buttonWrapper,
                        children: [
                            /*#__PURE__*/ _jsx(Primary, {
                                classNames: {
                                    button: styles["returnToCheckoutCta"]
                                },
                                isLoading: returnToCheckoutButtonLoading,
                                disabled: false,
                                onClick: function() {
                                    return handleReturnToCheckout();
                                },
                                size: "large",
                                children: locale.orderActive.buttonText
                            }),
                            /*#__PURE__*/ _jsx(Secondary, {
                                classNames: {
                                    button: styles["returnToCheckoutCta"]
                                },
                                isLoading: returnToCheckoutButtonLoading,
                                disabled: false,
                                onClick: function() {
                                    return handleCarNoLongerWanted();
                                },
                                size: "large",
                                children: locale.orderActive.cancelText
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx(Primary, {
                        classNames: {
                            button: styles["returnToCheckoutCta"]
                        },
                        isLoading: returnToCheckoutButtonLoading,
                        disabled: false,
                        onClick: function() {
                            return handleReturnToCheckout();
                        },
                        size: "large",
                        children: locale.orderActive.buttonText
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("div", {
                className: styles["basketBannerWrapper"],
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        "aria-label": "Close Active Order Banner",
                        "data-testid": "closeBanner",
                        className: styles["closeIconButton"],
                        onClick: function() {
                            return handleCloseBanner();
                        },
                        children: /*#__PURE__*/ _jsx(SvgIcon, {
                            name: "cross",
                            height: 12,
                            width: 12,
                            stroke: deepPurple,
                            strokeWidth: "2"
                        })
                    }),
                    /*#__PURE__*/ _jsx(BasketBannerVehicleInformation, {
                        sendDigitalDataEvent: sendDigitalDataEvent,
                        vehicle: activeOrder === null || activeOrder === void 0 ? void 0 : activeOrder.vehicle,
                        adminFeeFlag: adminFeeFlag,
                        adminFeeValueInPence: adminFeeValueInPence
                    }),
                    dontWantCarExperiment ? /*#__PURE__*/ _jsxs("div", {
                        className: styles.buttonWrapper,
                        children: [
                            /*#__PURE__*/ _jsx(Primary, {
                                classNames: {
                                    button: styles["returnToCheckoutCta"]
                                },
                                isLoading: returnToCheckoutButtonLoading,
                                disabled: false,
                                onClick: function() {
                                    return handleReturnToCheckout();
                                },
                                size: "large",
                                children: locale.orderActive.buttonText
                            }),
                            /*#__PURE__*/ _jsx(Secondary, {
                                classNames: {
                                    button: styles["returnToCheckoutCta"]
                                },
                                isLoading: returnToCheckoutButtonLoading,
                                disabled: false,
                                onClick: function() {
                                    return handleCarNoLongerWanted();
                                },
                                size: "large",
                                children: locale.orderActive.cancelText
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx(Primary, {
                        classNames: {
                            button: styles["returnToCheckoutCta"]
                        },
                        isLoading: returnToCheckoutButtonLoading,
                        disabled: false,
                        onClick: function() {
                            return handleReturnToCheckout();
                        },
                        size: "large",
                        children: locale.orderActive.buttonText
                    })
                ]
            })
        })
    }) : null;
}
export default Banner;
